/**
 * Centralized API Client with automatic retry logic for cold starts
 * Handles authentication, errors, and backend warm-up gracefully
 */

const MAX_RETRIES = 5;
const INITIAL_RETRY_DELAY = 2000; // 2 seconds
const MAX_RETRY_DELAY = 10000; // 10 seconds

class APIClient {
  constructor() {
    this.baseURL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:10000';
  }

  /**
   * Sleep utility for retry delays
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Calculate exponential backoff delay
   */
  getRetryDelay(attempt) {
    const delay = Math.min(INITIAL_RETRY_DELAY * Math.pow(2, attempt), MAX_RETRY_DELAY);
    return delay;
  }

  /**
   * Parse error response
   */
  async parseError(response) {
    try {
      const data = await response.json();
      return data.detail || data.error || data.message || response.statusText;
    } catch {
      return response.statusText || 'Request failed';
    }
  }

  /**
   * Core request method with retry logic
   */
  async request(endpoint, options = {}, retryCount = 0) {
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Handle successful responses
      if (response.ok) {
        // Handle 204 No Content
        if (response.status === 204) {
          return null;
        }
        return await response.json();
      }

      // Handle authentication errors (don't retry)
      if (response.status === 401) {
        const message = await this.parseError(response);
        throw { status: 401, message, retry: false };
      }

      // Handle client errors (don't retry)
      if (response.status >= 400 && response.status < 500) {
        const message = await this.parseError(response);
        throw { status: response.status, message, retry: false };
      }

      // Handle server errors (retry for cold starts)
      if (response.status >= 500 && retryCount < MAX_RETRIES) {
        const delay = this.getRetryDelay(retryCount);
        console.warn(
          `Server error (${response.status}). Retrying in ${delay}ms... (attempt ${retryCount + 1}/${MAX_RETRIES})`
        );
        await this.sleep(delay);
        return this.request(endpoint, options, retryCount + 1);
      }

      // Max retries reached or other server error
      const message = await this.parseError(response);
      throw { status: response.status, message, retry: false };

    } catch (error) {
      // Network errors or timeout - retry for cold starts
      if (error.retry === false) {
        throw error; // Don't retry errors we explicitly marked
      }

      if (retryCount < MAX_RETRIES) {
        const delay = this.getRetryDelay(retryCount);
        console.warn(
          `Network error: ${error.message}. Retrying in ${delay}ms... (attempt ${retryCount + 1}/${MAX_RETRIES})`
        );
        await this.sleep(delay);
        return this.request(endpoint, options, retryCount + 1);
      }

      // Max retries reached
      throw {
        status: 503,
        message: 'Service temporarily unavailable. Please try again.',
        retry: false,
        original: error
      };
    }
  }

  // Convenience methods
  async get(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'GET' });
  }

  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint, options = {}) {
    return this.request(endpoint, { ...options, method: 'DELETE' });
  }
}

// Export singleton instance
export const apiClient = new APIClient();
export default apiClient;
